/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getCookie, setCookie } from './cookie';
import * as url from 'url';

export const supportedLanguages = [
	'cs',
	'da',
	'de',
	'en',
	'es',
	'fr',
	'it',
	'nl',
	'nb',
	'pl',
	'pt',
	'fi',
	'sv',
	'tr',
	'ru',
	'zh-Hans',
	'zh-Hant',
	'ja',
	'ko',
];

/**
 * Sets the value of the CC Home language cookie.
 * @param {string} language the language code
 */
export function setCCHomeLangCookie(language: string) {
	if (supportedLanguages.indexOf(language) !== -1) {
		setCookie('creative-cloud-loc', language);
	}
}

/**
 * Returns the value of the CC Home language cookie.
 */
export function getCCHomeLangCookie() {
	return getCookie('creative-cloud-loc');
}

/**
 * Removes locale param but keeps other params
 * @return {string} url without locale
 */
export function removeLocale(inputUrl: string) {
	if (inputUrl) {
		const urlParams: any = url.parse(inputUrl, true).query;
		const filteredParams = Object.entries(urlParams)
			.map((pair) => {
				if (pair[0] !== 'locale') {
					return `${pair[0]}=${pair[1]}`;
				}
				return null;
			})
			.filter((item) => item !== null)
			.join('&');

		return `${inputUrl.split('?')[0]}${filteredParams ? `?${filteredParams}` : ''}`;
	}

	return inputUrl;
}

/**
 * Removes temp locale param but keeps other params
 * @return {string} url without temp locale
 */
export function removeTempLocale(inputUrl: string) {
	if (inputUrl) {
		const urlParams: any = url.parse(inputUrl, true).query;
		const filteredParams = Object.entries(urlParams)
			.map((pair) => {
				if (pair[0] !== 'tempLocale') {
					return `${pair[0]}=${pair[1]}`;
				}
				return null;
			})
			.filter((item) => item !== null)
			.join('&');

		return `${inputUrl.split('?')[0]}${filteredParams ? `?${filteredParams}` : ''}`;
	}

	return inputUrl;
}

/**
 * Handles Language change
 * @return {string} updated url
 */
export function handleLanguageChange(val) {
	setCCHomeLangCookie(val);
	const urlParams: any = url.parse(window.location.href, true).query;
	// If there's a temp locale and the user switches to a language using the footer, remove the temp locale so the page actually changes to the user's chosen language.
	if (urlParams.tempLocale) {
		// $FlowFixMe
		location.replace(removeTempLocale(window.location.href));
	} else if (urlParams.locale) {
		// $FlowFixMe
		location.replace(removeLocale(window.location.href));
	} else {
		// $FlowFixMe
		location.reload();
	}
}

export function replaceURLWithLocale(locale) {
	setCCHomeLangCookie(locale);

	const path = window.location.pathname.split('/');
	if (supportedLanguages.indexOf(path[1]) > -1) {
		path[1] = locale;
	} else {
		path.splice(1, 0, locale);
	}
	if (locale === 'en') {
		path.splice(1, 1);
	}
	const route = path.join('/');
	location.replace(`${location.origin}${route}`);
}
